import React from 'react';
import { formatNumber } from '../../lib/dashboard/mapOverview';
import { TopAgentsChart, type TopAgentDatum } from './TopAgentsChart';
import { DecorativeShape } from '../ui/DecorativeShape';
import { Button } from '../ui/Button';

interface AgentUpdatesSectionProps {
  topAgents: TopAgentDatum[];
  periodConversations: number;
  periodDays: number;
}

export const AgentUpdatesSection: React.FC<AgentUpdatesSectionProps> = ({
  topAgents,
  periodConversations,
  periodDays,
}) => {
  return (
    <section className="relative mx-auto mt-12 w-full max-w-[1360px] sm:mt-14 md:mt-16">
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
        <DecorativeShape className="absolute -left-16 top-[16%] h-[140px] w-[207px] opacity-20 sm:-left-10 sm:h-[185px] sm:w-[274px]" />
        <DecorativeShape className="absolute -left-[62px] top-[40%] h-[140px] w-[207px] opacity-20 sm:-left-8 sm:h-[185px] sm:w-[274px]" />
        <div className="absolute left-4 top-[24%] h-8 w-8 rounded-full bg-[radial-gradient(circle_at_28%_28%,rgb(var(--c-f9f4ff,249_244_255))_0%,rgb(var(--c-dcbef9,220_190_249))_55%,rgb(var(--c-8bb3ff,139_179_255))_100%)] opacity-80 sm:left-8 sm:h-10 sm:w-10" />
        <div className="absolute left-4 top-[46%] h-8 w-8 rounded-full bg-[radial-gradient(circle_at_28%_28%,rgb(var(--c-f9f4ff,249_244_255))_0%,rgb(var(--c-dcbef9,220_190_249))_55%,rgb(var(--c-8bb3ff,139_179_255))_100%)] opacity-80 sm:left-8 sm:h-10 sm:w-10" />
      </div>
      <div className="relative z-10 grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)] lg:gap-8">
        <div className="min-w-0">
          <h3 className="max-w-[500px] text-[2.3rem] font-semibold leading-[1.12] text-c-12152a sm:text-[2.9rem] lg:text-[3.5rem]">
            Stay updated on what&apos;s happening across your deployed agents
          </h3>
          <p className="mt-4 max-w-[520px] text-[1.05rem] leading-8 text-c-30354f sm:mt-5 sm:text-lg">
            Watch tasks unfold in real time as your pods learn, optimize, and collaborate autonomously.
          </p>
          <Button tone="custom"
            type="button"
            className="mt-7 rounded-xl border border-c-afc4f8 bg-white px-5 py-3 text-[1.1rem] font-medium leading-none text-c-20264d shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-c-4f71ea focus-visible:ring-offset-2 sm:text-base"
          >
            View Full Activity Log ↗
          </Button>

          <div className="mt-14 flex items-end gap-3 sm:mt-20">
            <p className="text-[4.1rem] font-semibold leading-none text-transparent [-webkit-text-stroke:1.6px_#8fa4ef] sm:text-[4.75rem]">
              {formatNumber(periodConversations)}
            </p>
            <p className="pb-2 text-base leading-tight text-c-a3a9be sm:text-lg">
              conversations handled
              <br />
              in the last {periodDays} days
            </p>
          </div>
        </div>

        <div className="h-full min-w-0">
          <TopAgentsChart data={topAgents} />
        </div>
      </div>
    </section>
  );
};
